<template>
  <div class="d-flex flex-column ga-4">
    <v-card variant="tonal" color="surface">
      <v-card-text>
        <div class="text-subtitle-2 font-weight-bold mb-2">
          {{ delivery.external_id }}
        </div>
        <div class="text-body-2 text-medium-emphasis">
          {{ formatDate(delivery.received_at) }}
        </div>
        <div class="d-flex flex-wrap ga-2 mt-3">
          <v-chip size="small" :color="statusMeta(delivery.status).color" :variant="statusMeta(delivery.status).variant">
            {{ delivery.status }}
          </v-chip>
          <v-chip color="secondary">
            {{ delivery.event_type || "-" }}
          </v-chip>
          <v-chip color="info">
            {{ delivery.source_system || "-" }}
          </v-chip>
          <v-chip
            v-if="delivery.privacy_indicator"
            size="small"
            prepend-icon="mdi-shield-account-outline"
            :color="privacyMeta.color"
            :variant="privacyMeta.variant"
          >
            {{ $t("ncrm.integrations.externalApi.deliveries.detail.privacyLabel") }}:
            {{ $t(`ncrm.integrations.externalApi.deliveries.detail.privacy.${delivery.privacy_indicator}`) }}
          </v-chip>
        </div>
      </v-card-text>
    </v-card>

    <v-card variant="outlined">
      <v-card-title>{{ $t("ncrm.integrations.externalApi.deliveries.detail.metadata") }}</v-card-title>
      <v-card-text class="ncrm-external-api-delivery-detail__meta-grid">
        <div
          v-for="entry in metadataEntries"
          :key="entry.key"
          class="ncrm-external-api-delivery-detail__meta-item"
        >
          <div class="text-caption text-medium-emphasis">
            {{ entry.label }}
          </div>
          <div class="text-body-2 font-weight-medium">
            {{ entry.value }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-alert
      v-if="delivery.error_message || delivery.error_code"
      type="error"
      density="comfortable"
    >
      <div class="font-weight-medium mb-1">
        {{ delivery.error_code || $t("ncrm.integrations.externalApi.deliveries.detail.errorLabel") }}
      </div>
      <div class="text-body-2">
        {{ delivery.error_message || "-" }}
      </div>
    </v-alert>

    <v-card variant="outlined">
      <v-card-title>{{ $t("ncrm.integrations.externalApi.deliveries.detail.rawBody") }}</v-card-title>
      <v-card-text>
        <pre class="ncrm-external-api-delivery-detail__pre">{{ pretty(delivery.raw_body_json) }}</pre>
      </v-card-text>
    </v-card>

    <v-card variant="outlined">
      <v-card-title>{{ $t("ncrm.integrations.externalApi.deliveries.detail.normalized") }}</v-card-title>
      <v-card-text>
        <pre class="ncrm-external-api-delivery-detail__pre">{{ pretty(delivery.normalized_payload_json) }}</pre>
      </v-card-text>
    </v-card>

    <v-card variant="outlined">
      <v-card-title>{{ $t("ncrm.integrations.externalApi.deliveries.detail.processingResult") }}</v-card-title>
      <v-card-text>
        <pre class="ncrm-external-api-delivery-detail__pre">{{ pretty(delivery.processing_result_json) }}</pre>
      </v-card-text>
    </v-card>

    <v-card variant="outlined">
      <v-card-title>{{ $t("ncrm.integrations.externalApi.deliveries.detail.requestHeaders") }}</v-card-title>
      <v-card-text>
        <pre class="ncrm-external-api-delivery-detail__pre">{{ pretty(delivery.request_headers_json) }}</pre>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {
  formatExternalApiDateTime,
  prettyPrintExternalApiJson,
  resolveDeliveryPrivacyMeta,
  resolveExternalApiStatusMeta,
} from "@/Utile/ncrmExternalApi.mjs";

export default {
  name: "NcrmExternalApiDeliveryDetailCard",
  props: {
    delivery: {
      type: Object,
      required: true,
    },
  },
  computed: {
    privacyMeta() {
      return resolveDeliveryPrivacyMeta(this.delivery.privacy_indicator);
    },
    metadataEntries() {
      return [
        {
          key: "endpoint",
          label: this.$t("ncrm.integrations.externalApi.deliveries.detail.fields.endpoint"),
          value: this.delivery.endpoint_name || this.delivery.endpoint_key || "-",
        },
        {
          key: "request_method",
          label: this.$t("ncrm.integrations.externalApi.deliveries.detail.fields.requestMethod"),
          value: this.delivery.request_method || "-",
        },
        {
          key: "content_type",
          label: this.$t("ncrm.integrations.externalApi.deliveries.detail.fields.contentType"),
          value: this.delivery.content_type || "-",
        },
        {
          key: "idempotency_key",
          label: this.$t("ncrm.integrations.externalApi.deliveries.detail.fields.idempotencyKey"),
          value: this.delivery.idempotency_key || "-",
        },
        {
          key: "occurred_at",
          label: this.$t("ncrm.integrations.externalApi.deliveries.detail.fields.occurredAt"),
          value: this.formatDate(this.delivery.occurred_at),
        },
        {
          key: "source_ip",
          label: this.$t("ncrm.integrations.externalApi.deliveries.detail.fields.sourceIp"),
          value: this.delivery.source_ip || "-",
        },
        {
          key: "user_agent",
          label: this.$t("ncrm.integrations.externalApi.deliveries.detail.fields.userAgent"),
          value: this.delivery.user_agent || "-",
        },
      ];
    },
  },
  methods: {
    pretty(value) {
      return prettyPrintExternalApiJson(value, "null");
    },
    formatDate(value) {
      return formatExternalApiDateTime(value);
    },
    statusMeta(status) {
      return resolveExternalApiStatusMeta(status);
    },
  },
};
</script>

<style scoped>
.ncrm-external-api-delivery-detail__meta-grid {
  display: grid;
  gap: 12px;
}

.ncrm-external-api-delivery-detail__meta-item {
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.04);
}

.ncrm-external-api-delivery-detail__pre {
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  font-size: 0.85rem;
}

@media (min-width: 960px) {
  .ncrm-external-api-delivery-detail__meta-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
